import cx from 'classnames';
import { isEmpty, orderBy, sumBy } from 'lodash';
import queryString from 'query-string';
import { useEffect, useMemo, useState } from 'react';
import { connect } from 'react-redux';
import { RouteComponentProps, withRouter } from 'react-router-dom';
import { compose, withHandlers, withState } from 'recompose';
import { AssignmentType } from 'models/enum';
import { AssignmentTopic, CaseStudy, NewAssignment, SimulationEhr, Step } from 'models/ui';
import { apiHelper, appHelper, assignmentHelper } from 'helpers';
import { resetCaseStudyDemoStorage, resetSimulationEhrDemoStorage } from 'helpers/caseStudy.helper';
import { instructorActions, instructorSelectors } from 'redux/ducks/instructor';
import { Avatar, ButtonLinkIcon, ProductFilter } from 'components/common';
import { ELSButton, ELSDataTable, ELSFlex, ELSFlexItem, ELSPageLoader, ELSRadio } from 'components/common/els';
import QuickView from 'components/common/quick-view/QuickView';
import './assignment.content.scss';

interface AssignmentContentProps {
  isLoading: boolean;
  assignmentTopics: AssignmentTopic[];
  content: CaseStudy[];
  selectedContent: CaseStudy | SimulationEhr;
  prevCaseStudyDemo: CaseStudy;
  prevSimulationEhrDemo: SimulationEhr;
  setSelectedContent: Function;
  setIsLoading: Function;
  onRowAction: Function;
  onRowSelection: Function;
  resetCaseStudyDemo: Function;
  resetSimulationEhrDemo: Function;
  courseId: string;
  steps: Step[];
  isBookOrganized: boolean;
  setNewAssignment: Function;
  newAssignment: NewAssignment;
}

export const AssignmentContent = (props: AssignmentContentProps & RouteComponentProps) => {
  const {
    assignmentTopics,
    onRowSelection,
    isLoading,
    setIsLoading,
    content,
    selectedContent,
    prevCaseStudyDemo,
    prevSimulationEhrDemo,
    setSelectedContent,
    resetCaseStudyDemo,
    onRowAction,
    courseId,
    newAssignment,
    resetSimulationEhrDemo
  } = props;

  const [visibleContent, setVisibleContent] = useState(content);
  const [isProductFiltersEnable, setProductFiltersEnable] = useState(false);
  const [productFilters, setProductFilters] = useState([]);
  const [productsToFilter, setProductsToFilter] = useState([]);

  const handleContent = (content) => {
    if (content) {
      onRowSelection(content);
      const { nextStep } = appHelper.getStepInfo(props.steps);
      props.history.push(nextStep.route);
    }
  };

  const handleCloseSideBar = () => {
    setSelectedContent(null);
    resetCaseStudyDemoStorage();
    resetSimulationEhrDemoStorage();
    resetCaseStudyDemo();
    resetSimulationEhrDemo();
  };

  useEffect(() => {
    const fetchContentWithProductMapping = async () => {
      setIsLoading(true);
      const { isStandalone, contentProductMapping, products } = await assignmentHelper.getAssignmentProductsMapping(courseId, newAssignment?.assignmentType);
      const newContent = content.map((item) => ({ ...item, isSelected: false, product: contentProductMapping[item.id] }));
      const prevSelectedContent = newAssignment.assignmentType === AssignmentType.CASE_STUDY ? prevCaseStudyDemo : prevSimulationEhrDemo;
      const parsedQuery = queryString.parse(props?.history?.location?.search);
      const { caseStudyId, simulationEhrId } = parsedQuery;
      if (simulationEhrId) {
        const simulationEhr = newContent.find(({ id }) => id === simulationEhrId);
        handleContent(simulationEhr);
      } else if (caseStudyId) {
        const caseStudy = newContent.find(({ id }) => id === caseStudyId);
        handleContent(caseStudy);
      }
      setProductFiltersEnable(isStandalone);
      setVisibleContent(newContent);
      setProductsToFilter(products);

      if (prevSelectedContent) {
        setSelectedContent(prevSelectedContent); // To-do: prevSelectedContent needs to be updated with simulation ehr when we work on preview(quick view),
      }

      setIsLoading(false);
    };

    fetchContentWithProductMapping().catch((err) => {
      apiHelper.showApiError(`can not get the content ${err}`);
    });
  }, [content]);

  useEffect(() => {
    const newContent = !isEmpty(productFilters)
      ? content.filter((item) => {
          return productFilters.includes(item?.product?.name);
        })
      : content;
    setVisibleContent(newContent);
  }, [productFilters]);

  const assignmentTitle = newAssignment.assignmentType === AssignmentType.CASE_STUDY ? 'SimChart Case Studies' : 'Simulation EHRs';
  const id = assignmentTopics[0]?.vtwId;
  const sorter = (assignment) => Number(assignment.sherpathModule?.match(/\d+/g)?.[0]);
  const comingSoonCount = sumBy(content, (a) => a.isComingSoon);
  const newTableData = useMemo(() => {
    const sortedContent = orderBy(visibleContent, sorter);
    const seenTitles = new Set();
    const practiceAssignmentTitle = 'SimChart Practice Assignment';
    const deduplicatedContent = sortedContent.filter((item) => {
      if (item.title === practiceAssignmentTitle) {
        const notSeenBefore = !seenTitles.has(practiceAssignmentTitle);
        seenTitles.add(practiceAssignmentTitle);
        return notSeenBefore;
      }
      return true;
    });
    const practiceAssignmentIndex = deduplicatedContent.findIndex((item) => item.title === practiceAssignmentTitle);
    const reorderedContent =
      practiceAssignmentIndex !== -1
        ? [deduplicatedContent[practiceAssignmentIndex], ...deduplicatedContent.slice(0, practiceAssignmentIndex), ...deduplicatedContent.slice(practiceAssignmentIndex + 1)]
        : deduplicatedContent;

    return reorderedContent.map((item) => ({
      ...item,
      isSelected: item.id === id,
      cssModifier: item.isComingSoon ? 'comingSoonStyle' : ''
    }));
  }, [id, visibleContent, content]);

  const radioRenderer = (row) => (
    <>
      <span id={`radio-label-${row.id}`} className="u-els-hide-visually">
        {row.sherpathModule}
      </span>
      <ELSRadio
        id={row.id}
        name={row.id}
        value={row.id}
        checked={row.isSelected}
        changeHandler={() => onRowSelection(row)}
        isDisabled={row.isComingSoon}
        aria-labelledby={`radio-label-${row.id}`}
      >
        <></>
      </ELSRadio>
    </>
  );

  const patientRenderer = (row) => (
    <ELSFlex middle left>
      <ELSFlexItem>
        <Avatar className="patient-avatar u-els-text-center" name={`${row?.patient?.firstName?.charAt(0)}${row?.patient?.lastName?.charAt(0)}`} />
      </ELSFlexItem>
      <ELSFlexItem>
        <span className="patient-name">{`${row?.patient?.firstName} ${row?.patient?.lastName}`}</span>
      </ELSFlexItem>
    </ELSFlex>
  );

  const chapterRenderer = (row) => <b>{row.sherpathModule}</b>;

  const actionsRenderer = (row) => {
    if (row.isComingSoon) {
      return (
        <ELSButton
          type="debuttonize"
          iconName="enlarge"
          iconPosition="right"
          iconSize="1x"
          text="Quick view"
          className={cx('c-els-button--with-icon c-els-button--middle c-els-button--with-icon-right', 'comingSoonStyle')}
        />
      );
    }

    return <ButtonLinkIcon iconName="enlarge" iconPosition="right" iconSize="1x" text="Quick view" onClick={() => onRowAction(row)} />;
  };

  return (
    <div className="assignment-content">
      {isLoading && <ELSPageLoader />}
      {!isLoading && isProductFiltersEnable && <ProductFilter setProductFilters={setProductFilters} products={productsToFilter} />}
      <ELSDataTable data={newTableData} cssModifier="c-els-table--row-highlight c-els-table--border-bottom">
        <column header=" " customRender={radioRenderer} />
        {props.isBookOrganized && <column header="Chapter" sortable field="chapter" customRender={chapterRenderer} />}
        <column header="Title" field="title" customRender={(row) => `${row.title}${row.isComingSoon ? ' (coming soon)' : ''}`} />
        <column header="Diagnosis" field="diagnosis" />
        {newAssignment.assignmentType === AssignmentType.CASE_STUDY && <column header="Concepts (Giddens)" field="concept" />}
        <column header="Patient" field="patient" customRender={patientRenderer} />
        <column header="Action" field="action" customRender={actionsRenderer} />
      </ELSDataTable>
      <p className="u-els-italic u-els-color-n9 u-els-padding">
        {`${content.length - comingSoonCount} ${assignmentTitle} available`}
        {comingSoonCount > 0 && `, ${comingSoonCount} ${assignmentTitle} coming soon`}
      </p>

      <QuickView
        selectedContent={selectedContent}
        initialShowPreview={!!prevCaseStudyDemo || !!prevSimulationEhrDemo}
        onSelectClick={() => handleContent(selectedContent)}
        onCloseSideBar={handleCloseSideBar}
        isBookOrganized={props.isBookOrganized}
        assignmentType={newAssignment.assignmentType}
      />
    </div>
  );
};

const mapStateToProps = (state) => ({
  assignmentTopics: instructorSelectors.getAssignmentTopics(state),
  prevCaseStudyDemo: instructorSelectors.getCaseStudyDemo(state),
  prevSimulationEhrDemo: instructorSelectors.getSimulationEhrDemo(state),
  isBookOrganized: instructorSelectors.getIsBookOrganized(state)
});

const mapDispatchToProps = (dispatch) => ({
  setNewAssignment: (payload) => dispatch(instructorActions.setNewAssignment(payload)),
  resetCaseStudyDemo: () => dispatch(instructorActions.setCaseStudyDemo(null)),
  resetSimulationEhrDemo: () => dispatch(instructorActions.setSimulationEhrDemo(null))
});

export const onRowSelectionHandler = (props) => (row) => {
  const { setNewAssignment, newAssignment } = props;
  const topic = { vtwId: row.id, text: row.diagnosis };
  const assignment = newAssignment?.assignmentType === AssignmentType.SIMULATION ? { simulationEhrId: row.id } : { caseStudyId: row.id };
  setNewAssignment({ assignmentTopics: [topic], title: row.title, ...assignment });
};

export const handleRowAction = (props) => (row) => {
  const { setSelectedContent } = props;
  setSelectedContent(row);
};

export default compose(
  withRouter,
  connect(mapStateToProps, mapDispatchToProps),
  withState('isLoading', 'setIsLoading', false),
  withState('selectedContent', 'setSelectedContent', null),
  withHandlers({
    onRowSelection: onRowSelectionHandler,
    onRowAction: handleRowAction
  })
)(AssignmentContent);
